import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { debounce } from 'lodash';
import { apiGetAllCustomer } from '@services/ManageCustomers/CustomerServices';
import { apiGetCategory } from '@services/ManageDevices/DeviceServices';
import {
  apiCreateDMA,
  apiDeleteDMA,
  apiGetAllDMA,
  apiGetDMAById,
  apiUpdateDMA
} from '@services/ManageDMA/DMAServices';
import { usePermission } from '@contexts/Auth/UsePermission';

const { Text } = Typography;

/* ------------------------------------------------------------------ */
/*  Small helper: async Select for customers                           */
/* ------------------------------------------------------------------ */
const CustomerSelect = ({ value, onChange, mode, placeholder, style, disabled }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCustomers = async (keyword = '') => {
    setLoading(true);
    try {
      const res = await apiGetAllCustomer({ page: 1, size: 50, keyWord: keyword || undefined });
      setOptions(
        (res.data?.data || []).map((c) => ({
          label: `[${c.code}] ${c.name}`,
          value: c.code,
          record: c
        }))
      );
    } catch (_) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(debounce(fetchCustomers, 500), []);

  useEffect(() => {
    fetchCustomers();
  }, []);

  return (
    <Select
      showSearch
      filterOption={false}
      onSearch={debouncedFetch}
      loading={loading}
      mode={mode}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={style}
      disabled={disabled}
      allowClear
      options={options}
    />
  );
};

/* ------------------------------------------------------------------ */
/*  Modal Create / Edit                                                 */
/* ------------------------------------------------------------------ */
const ModalDMA = ({ open, onClose, onSuccess, editingId }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branchOptions, setBranchOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(undefined);

  const fetchBranches = async () => {
    try {
      const res = await apiGetCategory({ type: 'CN', page: 1, size: 1000 });
      setBranchOptions((res.data?.data || []).map((b) => ({ label: b.name, value: b.value })));
    } catch (_) {
      // ignore
    }
  };

  const fetchGroups = async (branchCode) => {
    try {
      const res = await apiGetCategory({
        type: 'TQL',
        parent_level_1_id: branchCode || undefined,
        page: 1,
        size: 1000
      });
      setGroupOptions((res.data?.data || []).map((g) => ({ label: g.name, value: g.value })));
    } catch (_) {
      // ignore
    }
  };

  const fetchCustomers = async (keyword = '') => {
    setLoadingCustomers(true);
    try {
      const res = await apiGetAllCustomer({ page: 1, size: 50, keyWord: keyword || undefined });
      setCustomerOptions(
        (res.data?.data || []).map((c) => ({
          label: `[${c.code}] ${c.name}`,
          value: c.code,
          branchCode: c.branch_code,
          branch: c.branch,
          manageGroupCode: c.manage_group_code,
          manageGroup: c.manage_group
        }))
      );
    } catch (_) {
      // ignore
    } finally {
      setLoadingCustomers(false);
    }
  };

  const debouncedFetchCustomers = useCallback(debounce(fetchCustomers, 500), []);

  useEffect(() => {
    if (open) {
      fetchBranches();
      fetchCustomers();
      if (editingId) {
        setLoading(true);
        apiGetDMAById(editingId)
          .then((res) => {
            const d = res.data?.data;
            if (d) {
              form.setFieldsValue({
                name: d.name,
                code: d.code,
                master_customer_code: d.master_customer_code || [],
                customer_codes: d.customer_codes || [],
                branch_code: d.branch_code,
                manage_group_code: d.manage_group_code
              });
              setSelectedBranch(d.branch_code);
              fetchGroups(d.branch_code);
            }
          })
          .finally(() => setLoading(false));
      } else {
        form.resetFields();
        setSelectedBranch(undefined);
        fetchGroups();
      }
    }
  }, [open, editingId]);

  useEffect(() => {
    fetchGroups(selectedBranch);
  }, [selectedBranch]);

  const handleMasterCustomerChange = (value) => {
    const selected = customerOptions.find((c) => c.value === value);
    if (selected) {
      form.setFieldsValue({
        branch_code: selected.branchCode,
        manage_group_code: selected.manageGroupCode
      });
      setSelectedBranch(selected.branchCode);
      // store helper fields for submission
      form.setFieldsValue({ _branch: selected.branch, _manage_group: selected.manageGroup });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const branchOpt = branchOptions.find((b) => b.value === values.branch_code);
      const groupOpt = groupOptions.find((g) => g.value === values.manage_group_code);
      const masterCodes = values.master_customer_code || [];
      const masterNames = masterCodes.map(
        (code) => customerOptions.find((c) => c.value === code)?.label || code
      );

      const payload = {
        name: values.name,
        code: values.code,
        master_customer_code: masterCodes,
        master_customer_name: masterNames,
        customer_codes: values.customer_codes || [],
        branch_code: values.branch_code,
        branch: branchOpt?.label || values.branch_code,
        manage_group_code: values.manage_group_code,
        manage_group: groupOpt?.label || values.manage_group_code
      };

      if (editingId) {
        await apiUpdateDMA(editingId, payload);
        message.success('Cập nhật DMA thành công');
      } else {
        await apiCreateDMA(payload);
        message.success('Tạo DMA thành công');
      }
      onSuccess();
    } catch (err) {
      if (err?.errorFields) return; // validation error, stay open
      const msg = err?.response?.data?.message || 'Có lỗi xảy ra';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={editingId ? 'Chỉnh sửa DMA' : 'Tạo mới DMA'}
      width={700}
      onOk={handleSubmit}
      okText={editingId ? 'Cập nhật' : 'Tạo mới'}
      cancelText="Huỷ"
      confirmLoading={saving}
      destroyOnClose>
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          {/* hidden helper fields */}
          <Form.Item name="_branch" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="_manage_group" hidden>
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Tên DMA"
                name="name"
                rules={[{ required: true, message: 'Nhập tên DMA' }]}>
                <Input placeholder="Nhập tên DMA" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Mã DMA" name="code">
                <Input placeholder="Tự động sinh" disabled />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Đồng hồ tổng (Khách hàng)"
            name="master_customer_code"
            rules={[{ required: true, message: 'Chọn ít nhất một đồng hồ tổng' }]}
            tooltip="Chọn các khách hàng làm đồng hồ tổng. Nhà máy và Tổ quản lý sẽ tự động cập nhật theo máy đầu tiên.">
            <Select
              showSearch
              filterOption={false}
              onSearch={debouncedFetchCustomers}
              loading={loadingCustomers}
              mode="multiple"
              placeholder="Tìm kiếm và chọn khách hàng"
              onChange={handleMasterCustomerChange}
              allowClear
              options={customerOptions}
            />
          </Form.Item>

          <Form.Item
            label="Đồng hồ khách hàng"
            name="customer_codes"
            tooltip="Chọn nhiều khách hàng thuộc DMA này">
            <Select
              showSearch
              filterOption={false}
              onSearch={debouncedFetchCustomers}
              loading={loadingCustomers}
              mode="multiple"
              placeholder="Tìm kiếm và chọn khách hàng"
              allowClear
              options={customerOptions}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Nhà máy" name="branch_code">
                <Select
                  placeholder="Chọn Nhà máy"
                  options={branchOptions}
                  allowClear
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={(val) => setSelectedBranch(val)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Tổ quản lý" name="manage_group_code">
                <Select
                  placeholder="Chọn tổ quản lý"
                  options={groupOptions}
                  allowClear
                  showSearch
                  filterOption={(input, opt) =>
                    (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/*  Modal Detail (read-only)                                           */
/* ------------------------------------------------------------------ */
const ModalDMADetail = ({ open, onClose, data }) => {
  if (!data) return null;
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Chi tiết DMA: ${data.name}`}
      footer={null}
      width={600}>
      <Row gutter={[12, 8]} style={{ marginTop: 8 }}>
        <Col span={8}>
          <Text type="secondary">Tên DMA</Text>
        </Col>
        <Col span={16}>
          <Text strong>{data.name}</Text>
        </Col>
        <Col span={8}>
          <Text type="secondary">Mã DMA</Text>
        </Col>
        <Col span={16}>
          <Text strong>{data.code}</Text>
        </Col>
        <Col span={8}>
          <Text type="secondary">Đồng hồ tổng</Text>
        </Col>
        <Col span={16}>
          <Space direction="vertical" size={2}>
            {(Array.isArray(data.master_customer_code)
              ? data.master_customer_code
              : data.master_customer_code
              ? [data.master_customer_code]
              : []
            ).map((code, i) => {
              const names = Array.isArray(data.master_customer_name)
                ? data.master_customer_name
                : data.master_customer_name
                ? [data.master_customer_name]
                : [];
              return (
                <Space key={code} size={4}>
                  <Tag color="blue">{code}</Tag>
                  {names[i] && <Text type="secondary">{names[i]}</Text>}
                </Space>
              );
            })}
          </Space>
        </Col>
        <Col span={8}>
          <Text type="secondary">Nhà máy</Text>
        </Col>
        <Col span={16}>{data.branch || data.branch_code || '—'}</Col>
        <Col span={8}>
          <Text type="secondary">Tổ quản lý</Text>
        </Col>
        <Col span={16}>{data.manage_group || data.manage_group_code || '—'}</Col>
        <Col span={8}>
          <Text type="secondary">Số đồng hồ KH</Text>
        </Col>
        <Col span={16}>{data.customer_count || (data.customer_codes?.length ?? 0)}</Col>
        <Col span={8}>
          <Text type="secondary">Mã KH thành phần</Text>
        </Col>
        <Col span={16}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(data.customer_codes || []).map((code) => (
              <Tag key={code}>{code}</Tag>
            ))}
          </div>
        </Col>
        <Col span={8}>
          <Text type="secondary">Ngày tạo</Text>
        </Col>
        <Col span={16}>
          {data.created_at ? new Date(data.created_at).toLocaleString('vi-VN') : '—'}
        </Col>
        <Col span={8}>
          <Text type="secondary">Ngày cập nhật</Text>
        </Col>
        <Col span={16}>
          {data.updated_at ? new Date(data.updated_at).toLocaleString('vi-VN') : '—'}
        </Col>
      </Row>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/*  Modal: danh sách khách hàng của DMA                               */
/* ------------------------------------------------------------------ */
const ModalCustomerList = ({ open, codes, dmaName, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !codes?.length) {
      setData([]);
      return;
    }
    setLoading(true);
    // fetch all, filter by codes on client side
    apiGetAllCustomer({ page: 1, size: 1000, codes: codes.join(',') })
      .then((res) => {
        const rows = res.data?.data || [];
        // also accept server that doesn't support codes param
        const filtered = rows.filter((r) => codes.includes(r.code));
        setData(filtered.length ? filtered : rows);
      })
      .catch(() => message.error('Không tải được danh sách khách hàng'))
      .finally(() => setLoading(false));
  }, [open, codes]);

  const columns = [
    {
      title: 'STT',
      width: 55,
      align: 'center',
      render: (_, __, i) => i + 1
    },
    {
      title: 'DBĐD',
      dataIndex: 'code',
      width: 120,
      render: (v) => <Tag color="blue">{v}</Tag>
    },
    { title: 'Tên KH', dataIndex: 'name', width: 200 },
    { title: 'Nhà máy', dataIndex: 'branch', width: 110 },
    { title: 'TQL', dataIndex: 'manage_group', width: 110 },
    { title: 'Địa chỉ', dataIndex: 'address', width: 220 },
    { title: 'Số ĐT', dataIndex: 'phone', width: 110 }
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <Text strong style={{ color: '#0051A9' }}>
          Danh sách đồng hồ khách hàng &mdash; {dmaName}
        </Text>
      }
      width={900}
      destroyOnClose>
      <Spin spinning={loading}>
        <Table
          rowKey="code"
          size="small"
          columns={columns}
          dataSource={data}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 'max-content', y: 400 }}
        />
      </Spin>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/*  Main List Component                                                 */
/* ------------------------------------------------------------------ */
export default function DMAList() {
  const { dma } = usePermission();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);

  const [customerListOpen, setCustomerListOpen] = useState(false);
  const [customerListRecord, setCustomerListRecord] = useState(null);

  const fetchData = async (opts = {}) => {
    setLoading(true);
    try {
      const res = await apiGetAllDMA({
        page: opts.page ?? page,
        size: opts.pageSize ?? pageSize,
        keyWord: opts.keyword !== undefined ? opts.keyword : keyword
      });
      setList(res.data?.data || []);
      setTotal(res.data?.meta?.total || 0);
    } catch (_) {
      message.error('Không tải được danh sách DMA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, keyword]);

  const handleSearch = () => {
    setKeyword(searchInput);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setKeyword('');
    setPage(1);
  };

  const handleCreate = () => {
    navigate('/dma/add');
  };

  const handleEdit = (record) => {
    navigate(`/dma/edit/${record.id}`);
  };

  const handleViewDetail = (record) => {
    setDetailData(record);
    setDetailOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await apiDeleteDMA(id);
      message.success('Xoá DMA thành công');
      fetchData();
    } catch (_) {
      message.error('Xoá DMA thất bại');
    }
  };

  const columns = [
    {
      title: 'STT',
      width: 60,
      render: (_, __, index) => (page - 1) * pageSize + index + 1
    },
    {
      title: 'Tên DMA',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => handleViewDetail(record)}>
          {text}
        </Button>
      )
    },
    {
      title: 'Mã DMA',
      dataIndex: 'code',
      key: 'code',
      render: (v) => <Tag color="blue">{v}</Tag>
    },
    {
      title: 'Đồng hồ tổng',
      dataIndex: 'master_customer_code',
      key: 'master_customer_code',
      render: (codes, record) => {
        const codeArr = Array.isArray(codes) ? codes : codes ? [codes] : [];
        const nameArr = Array.isArray(record.master_customer_name)
          ? record.master_customer_name
          : record.master_customer_name
          ? [record.master_customer_name]
          : [];
        return (
          <Space direction="vertical" size={2}>
            {codeArr.map((code, i) => (
              <Space key={code} size={4}>
                {/* <Tag color="geekblue">{code}</Tag> */}
                {nameArr[i] && (
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    {nameArr[i]}
                  </Text>
                )}
              </Space>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Nhà máy',
      dataIndex: 'branch',
      key: 'branch',
      render: (v, record) => v || record.branch_code || '—'
    },
    {
      title: 'Tổ quản lý',
      dataIndex: 'manage_group',
      key: 'manage_group',
      render: (v, record) => v || record.manage_group_code || '—'
    },
    {
      title: 'Số đồng hồ KH',
      dataIndex: 'customer_count',
      key: 'customer_count',
      align: 'center',
      render: (v, record) => {
        const count = v ?? record.customer_codes?.length ?? 0;
        const clickable = record.customer_codes?.length > 0;
        return (
          <Tag
            color={clickable ? 'blue' : 'default'}
            style={{ cursor: clickable ? 'pointer' : 'default' }}
            onClick={() => {
              if (clickable) {
                setCustomerListRecord(record);
                setCustomerListOpen(true);
              }
            }}>
            {count}
          </Tag>
        );
      }
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => (v ? new Date(v).toLocaleDateString('vi-VN') : '—')
    },
    {
      title: 'Ngày cập nhật',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (v) => (v ? new Date(v).toLocaleDateString('vi-VN') : '—')
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            title="Xem chi tiết"
          />
          {dma?.edit && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              title="Chỉnh sửa"
            />
          )}
          {dma?.delete && (
            <Popconfirm
              title="Bạn có chắc chắn muốn xoá DMA này?"
              okText="Xoá"
              okType="danger"
              cancelText="Huỷ"
              onConfirm={() => handleDelete(record.id)}>
              <Button type="text" danger icon={<DeleteOutlined />} title="Xoá" />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <>
      {/* Title + action buttons */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Typography.Title level={4} style={{ color: '#0051A9', margin: 0 }}>
            Quản lý DMA
          </Typography.Title>
        </Col>
        <Col>
          {dma?.create && (
            <Button
              icon={<PlusOutlined />}
              onClick={handleCreate}
              style={{
                color: '#0051A9',
                height: 36,
                backgroundColor: '#E8EBEF',
                borderColor: '#E8EBEF',
                fontSize: 14
              }}>
              Tạo mới DMA
            </Button>
          )}
        </Col>
      </Row>

      <Card bodyStyle={{ padding: '8px' }}>
        {/* Search bar */}
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={14}>
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Tìm kiếm tên, mã DMA, đồng hồ tổng..."
              onPressEnter={handleSearch}
              style={{ width: '100%', height: 36 }}
            />
          </Col>
          <Col xs={24} md={10}>
            <Space>
              <Button icon={<ClearOutlined />} onClick={handleClearSearch} style={{ height: 36 }}>
                Xoá lọc
              </Button>
              <Button
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{
                  height: 36,
                  backgroundColor: '#0051A9',
                  borderColor: '#0051A9',
                  color: '#fff'
                }}>
                Tìm kiếm
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `Tổng ${t} bản ghi`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          }
        }}
      />

      {/* Detail Modal */}
      <ModalDMADetail open={detailOpen} onClose={() => setDetailOpen(false)} data={detailData} />
      <ModalCustomerList
        open={customerListOpen}
        codes={customerListRecord?.customer_codes || []}
        dmaName={customerListRecord?.name || ''}
        onClose={() => setCustomerListOpen(false)}
      />
    </>
  );
}
